import { Inject, Injectable } from "@angular/core";
import { AuthService, AuthStore } from "app/auth";
import { ChatLifecycleService, ChatOpenStore } from "app/chat";
import { AppHostService, AppParentService } from "app/core";
import { ExternalEventsService } from "app/core/external-events.service";
import { distinctUntilChanged, filter, Observable, take, tap } from "rxjs";
import { AVAILABILITY_STORE, AvailabilityState, AvailabilityStore } from "./availability";

/** A service used to retrieve and interact with all the states of the application */
@Injectable({ providedIn: "root" })
export class AppStateService {
  private hasInitialized = false;

  constructor(
    @Inject(AVAILABILITY_STORE) private availabilityStore: AvailabilityStore,

    private readonly chatLifecycleService: ChatLifecycleService,
    private readonly parentService: AppParentService,
    private readonly hostService: AppHostService,
    private readonly chatOpenStore: ChatOpenStore,
    private readonly externalEventsService: ExternalEventsService,
    private readonly authService: AuthService,
    private readonly authStore: AuthStore
  ) {}

  /** Initializes all the states in the application, for the states that are required */
  public initialize(): void {
    if (this.hasInitialized) {
      return;
    }
    this.hasInitialized = true;

    this.authStore.state$
      .pipe(
        filter((state) => state.status === "not-required" || state.status === "authenticated"),
        take(1),
        tap(() => this.availabilityStore.initialize())
      )
      .subscribe();
    this.authService.initialize();
    this.chatLifecycleService.initialize();
    this.hostService.isLoaderReady$
      .pipe(
        filter((ready) => ready === true),
        distinctUntilChanged(),
        tap(() => {
          this.parentService.updateHostDimensions();
          this.parentService.setBotVisible(this.chatOpenStore.isOpen() ?? false);
        })
      )
      .subscribe();

    // Subscribe to all listeners to handle incoming messages from loader functions
    this.externalEventsService.allListeners$.subscribe();

    this.chatOpenStore.isOpen$
      .pipe(
        take(1),
        tap((isOpen) => this.parentService.setBotVisible(isOpen))
      )
      .subscribe();
  }

  /** @return Whether the application is available for the current user */
  get available$(): Observable<AvailabilityState> {
    return this.availabilityStore.available$;
  }
}
